/**
 * RecordsPage - Main page for v4 Records view
 *
 * Atomic level: Page
 * Composes: CollectionHeader, FilterStrip, RecordsTable
 *
 * This is the main container page that brings together:
 * - Global Filter Strip (GO-35) with date range, dimensions, regulatory flags
 * - Detection summary showing vulnerable customers and eligible complaints counts
 * - Records display (table or card view)
 *
 * Requires a collectionId - records are always viewed in collection context.
 *
 * Following GO UI Design System patterns
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation, useSearchParams } from 'react-router-dom';
import { Search, Loader2, AlertCircle, ChevronRight, FileBarChart, BarChart3 } from 'lucide-react';
import { useNavigation } from '../../../contexts/NavigationContext';
import { cn } from '../../../lib/utils';
import { RecordsTable, RecordDetail, CollectionHeader, CollectionSettings, ConductScoreIndicator, PaginationControls, MonitoringDot, QueueTabs } from './components';
import type { QueueType } from './components';
import { FilterStrip, useFilterState, useFilterOptions, type FilterConfig } from './filter';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Timestamp } from '../../ui/timestamp';
import IconWrapper from '../../ui/IconWrapper';
import { FiGrid, FiList } from 'react-icons/fi';
import type { RecordCollection } from '../../../types/record';
import type { RecordFilters } from '../../../services/types';
import {
  useCollection,
  useRecords,
  useConfirmComplaint,
  useRejectComplaint,
  useUpdateCollectionSettings,
  useReclassifyRecord,
  useStartSimulation,
} from '../../../hooks/useRecords';
import { useOrganizationDisplaySettings } from '../../../hooks/useOrganizationDisplaySettings';
import { RootCauseHistoricalBadge, RootCauseStatusBanner } from '../common/components';
import { toast } from 'sonner';

type ViewMode = 'table' | 'cards';

interface RecordsPageProps {
  className?: string;
  /** Collection ID - defaults to route param if not provided */
  collectionId?: string;
}

/**
 * Map frontend filter state to API RecordFilters
 */
type SortableColumn = 'conduct_score' | 'received_at' | 'customer_name';

const mapFiltersToApiParams = (
  filters: FilterConfig,
  searchTerm: string,
  sortBy: RecordFilters['sortBy'] = 'conduct_score',
  sortOrder: RecordFilters['sortOrder'] = 'desc'
): RecordFilters => {
  const apiFilters: RecordFilters = {};

  if (searchTerm) {
    apiFilters.search = searchTerm;
  }

  // Conduct score range
  if (filters.conductScoreMin !== undefined && filters.conductScoreMin > 1) {
    apiFilters.conductScoreMin = filters.conductScoreMin;
  }
  if (filters.conductScoreMax !== undefined && filters.conductScoreMax < 10) {
    apiFilters.conductScoreMax = filters.conductScoreMax;
  }

  // Date range from Global Filter Strip
  // For custom ranges, only send when both dates are present
  if (filters.dateRange?.preset) {
    if (filters.dateRange.preset === 'custom') {
      if (filters.dateRange.from && filters.dateRange.to) {
        apiFilters.dateRangePreset = filters.dateRange.preset;
        apiFilters.dateFrom = filters.dateRange.from;
        apiFilters.dateTo = filters.dateRange.to;
      }
    } else {
      apiFilters.dateRangePreset = filters.dateRange.preset;
    }
  }

  // Business context filters (with logic mode)
  if (filters.businessContext?.productCategories?.values?.length) {
    apiFilters.productCategories = filters.businessContext.productCategories.values;
    if (filters.businessContext.productCategories.logic !== 'or') {
      apiFilters.productCategoriesLogic = filters.businessContext.productCategories.logic;
    }
  }
  if (filters.businessContext?.channels?.values?.length) {
    apiFilters.channels = filters.businessContext.channels.values;
    if (filters.businessContext.channels.logic !== 'or') {
      apiFilters.channelsLogic = filters.businessContext.channels.logic;
    }
  }
  if (filters.businessContext?.journeyStages?.values?.length) {
    apiFilters.journeyStages = filters.businessContext.journeyStages.values;
    if (filters.businessContext.journeyStages.logic !== 'or') {
      apiFilters.journeyStagesLogic = filters.businessContext.journeyStages.logic;
    }
  }

  // Dimensions (with logic mode, default OR)
  if (filters.dimensions?.values?.length) {
    apiFilters.dimensions = filters.dimensions.values;
    if (filters.dimensions.logic !== 'or') {
      apiFilters.dimensionsLogic = filters.dimensions.logic;
    }
  }

  // Regulatory flags (with logic mode, default AND)
  if (filters.regulatoryFlags?.values?.length) {
    apiFilters.regulatoryFlags = filters.regulatoryFlags.values;
    if (filters.regulatoryFlags.logic !== 'and') {
      apiFilters.regulatoryFlagsLogic = filters.regulatoryFlags.logic;
    }
  }

  apiFilters.sortBy = sortBy;
  apiFilters.sortOrder = sortOrder;

  return apiFilters;
};

/**
 * Map API Collection to frontend RecordCollection for CollectionHeader
 */
const mapCollectionToRecordCollection = (
  collection: { id: string; name: string },
  recordCount: number
): RecordCollection => ({
  id: collection.id,
  name: collection.name,
  recordCount,
  medianConductScore: 5, // Will be calculated from records
  rootCauses: [], // Populated from records
});

const RecordsPage: React.FC<RecordsPageProps> = ({ className, collectionId: propCollectionId }) => {
  // Get collection ID from route params or prop
  const { collectionId: routeCollectionId } = useParams<{ collectionId: string }>();
  const collectionId = propCollectionId || routeCollectionId;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateWithReferrer } = useNavigation();
  const { data: organizationDisplaySettings } = useOrganizationDisplaySettings();
  const rootCauseClassificationEnabled =
    organizationDisplaySettings?.root_cause_classification_enabled !== false;

  // Records state
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortBy, setSortBy] = useState<SortableColumn>('conduct_score');
  const [sortOrder, setSortOrder] = useState<RecordFilters['sortOrder']>('desc');
  const [activeQueue, setActiveQueue] = useState<QueueType>('complaints');

  // Pagination state - persisted in URL params
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '50', 10);

  const setPage = useCallback((newPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(newPage));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setPageSize = useCallback((newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('pageSize', String(newPageSize));
      next.set('page', '1'); // Reset to page 1 when changing page size
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Global Filter Strip state (GO-35)
  const filterState = useFilterState();

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset page when search changes
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);


  // =============================================================================
  // Data Fetching with React Query
  // =============================================================================

  // Fetch collection data
  const {
    data: collectionData,
    isLoading: isLoadingCollection,
    error: collectionError,
  } = useCollection(collectionId || '', { enabled: !!collectionId });

  // Fetch filter options for the Global Filter Strip (GO-35)
  const { data: filterOptions } = useFilterOptions();

  // Detection settings from API (needed for filter mapping)
  const complaintDetectionEnabled = collectionData?.collection.complaintDetectionEnabled ?? true;
  const vulnerabilityDetectionEnabled = collectionData?.collection.vulnerabilityDetectionEnabled ?? true;

  // Build API filters for records query
  const apiFilters = useMemo(() => {
    const base = mapFiltersToApiParams(
      filterState.filters,
      debouncedSearchTerm,
      sortBy,
      sortOrder
    );

    // Queue tab filter — merge into regulatoryFlags and force AND so the
    // tab constraint holds regardless of the user's regulatoryFlagsLogic
    // choice in the filter strip (otherwise an "or" toggle could let the
    // Eligible Complaints tab show vulnerability-only records).
    const queueFlag =
      activeQueue === 'complaints'
        ? 'complaint'
        : activeQueue === 'vulnerability'
        ? 'vulnerability'
        : null;

    if (queueFlag) {
      const existing = base.regulatoryFlags ?? [];
      base.regulatoryFlags = existing.includes(queueFlag as any)
        ? existing
        : [...existing, queueFlag as any];
      base.regulatoryFlagsLogic = 'and';
    }

    return base;
  }, [filterState.filters, debouncedSearchTerm, sortBy, sortOrder, activeQueue]);

  // Fetch records with pagination and filters
  const {
    data: recordsData,
    isLoading: isLoadingRecords,
    error: recordsError,
    isFetching: isFetchingRecords,
  } = useRecords(collectionId || '', apiFilters, page, pageSize, { enabled: !!collectionId });

  // Mutations
  const confirmComplaintMutation = useConfirmComplaint();
  const rejectComplaintMutation = useRejectComplaint();
  const updateSettingsMutation = useUpdateCollectionSettings();
  const reclassifyMutation = useReclassifyRecord();
  const startSimulationMutation = useStartSimulation();

  // Track which record is being reclassified or simulated
  const [reclassifyingRecordId, setReclassifyingRecordId] = useState<string | null>(null);
  const [startingSimulationRecordId, setStartingSimulationRecordId] = useState<string | null>(null);

  // Extract data from query results
  const totalRecords = recordsData?.pagination?.totalItems ?? 0;
  const collection = collectionData
    ? mapCollectionToRecordCollection(
        collectionData.collection,
        totalRecords
      )
    : null;

  // Handle detection setting changes with API persistence
  const handleComplaintDetectionChange = async (enabled: boolean) => {
    if (collectionId) {
      try {
        await updateSettingsMutation.mutateAsync({
          collectionId,
          settings: { complaintDetectionEnabled: enabled },
        });
      } catch (error) {
        console.error('Failed to update complaint detection setting:', error);
      }
    }
  };

  const handleVulnerabilityDetectionChange = async (enabled: boolean) => {
    if (collectionId) {
      try {
        await updateSettingsMutation.mutateAsync({
          collectionId,
          settings: { vulnerabilityDetectionEnabled: enabled },
        });
      } catch (error) {
        console.error('Failed to update vulnerability detection setting:', error);
      }
    }
  };

  const handleBillingTagSave = async (billingTag: string | null) => {
    if (collectionId) {
      try {
        await updateSettingsMutation.mutateAsync({
          collectionId,
          settings: { billingTag },
        });
        toast.success(billingTag ? 'Billing tag updated' : 'Billing tag cleared');
      } catch (error) {
        console.error('Failed to update billing tag:', error);
        toast.error('Failed to update billing tag', {
          description: 'Please try again later.',
        });
        throw error;
      }
    }
  };

  const handleSort = (column: SortableColumn) => {
    if (!column) return;
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // State for confirmed complaints (tracked locally for optimistic UI)
  const [confirmedComplaints, setConfirmedComplaints] = useState<Set<string>>(new Set());

  const handleRecordClick = (recordId: string) => {
    setExpandedRecordId((prev) => (prev === recordId ? null : recordId));
  };

  const handleConfirmComplaint = async (recordId: string) => {
    // Optimistic UI update
    setConfirmedComplaints((prev) => new Set(prev).add(recordId));

    // Persist the change via API
    try {
      await confirmComplaintMutation.mutateAsync({ recordId });
    } catch (error) {
      console.error('Failed to confirm complaint:', error);
      // Rollback optimistic update on error
      setConfirmedComplaints((prev) => {
        const newSet = new Set(prev);
        newSet.delete(recordId);
        return newSet;
      });
    }
  };

  const handleRejectComplaint = async (recordId: string) => {
    // Optimistic UI update
    setConfirmedComplaints((prev) => {
      const newSet = new Set(prev);
      newSet.delete(recordId);
      return newSet;
    });

    // Persist the change via API
    try {
      await rejectComplaintMutation.mutateAsync({ recordId });
    } catch (error) {
      console.error('Failed to reject complaint:', error);
      // Rollback optimistic update on error
      setConfirmedComplaints((prev) => new Set(prev).add(recordId));
    }
  };

  const handleReclassify = async (recordId: string) => {
    setReclassifyingRecordId(recordId);
    try {
      await reclassifyMutation.mutateAsync({ recordId });
      toast.success('Reclassification started', {
        description: 'The record will be updated when processing completes.',
      });
    } catch (error) {
      console.error('Failed to reclassify record:', error);
      toast.error('Reclassification failed', {
        description: 'Please try again later.',
      });
    } finally {
      setReclassifyingRecordId(null);
    }
  };

  const handleStartSimulation = async (recordId: string) => {
    setStartingSimulationRecordId(recordId);
    try {
      const simulation = await startSimulationMutation.mutateAsync({ recordId });
      toast.success('Case guidance started', {
        description: 'Analyzing against historical ombudsman decisions...',
      });
      // Navigate to simulation workspace, preserving V4 route as referrer
      navigateWithReferrer(`/v3/ombudsman/simulations/${simulation.id}`, location.pathname);
    } catch (error) {
      console.error('Failed to start simulation:', error);
      toast.error('Case guidance failed to start', {
        description: 'Please try again later.',
      });
      setStartingSimulationRecordId(null);
    }
  };

  // Get source records from API
  const sourceRecords = useMemo(() => {
    return recordsData?.records ?? [];
  }, [recordsData?.records]);

  // Filter records - apply detection settings, then merge confirmed status
  // Note: When detection features are disabled, we filter client-side to hide those records.
  // When detection filters (tabs) are selected, filtering is done server-side via apiFilters.
  // This client-side filtering is only for when features are disabled at the collection level.
  const baseFilteredRecords = useMemo(() => {
    let records = [...sourceRecords];

    // Apply detection settings (client-side filtering when features are disabled)
    // This is separate from tab filters - it's for when the collection has detection disabled
    if (!complaintDetectionEnabled) {
      // Filter out records that are primarily complaint-focused when feature is disabled
      records = records.filter(
        (r) => r.eligibleComplaintDetection?.outcome !== 'complaint_disp'
      );
    }
    if (!vulnerabilityDetectionEnabled) {
      // Filter out records with vulnerability detection when feature is disabled
      records = records.filter((r) => !r.vulnerabilityDetection?.detected);
    }

    // Server-side filtering (pagination, search, date ranges, tab filters) is applied via apiFilters
    // Merge in confirmed complaint status
    return records.map((r) => ({
      ...r,
      isConfirmedComplaint: r.isConfirmedComplaint || confirmedComplaints.has(r.id),
    }));
  }, [sourceRecords, complaintDetectionEnabled, vulnerabilityDetectionEnabled, confirmedComplaints]);

  // Detection counts from aggregate stats (server-provided totals)
  const vulnerableCount = recordsData?.aggregateStats?.vulnerableCount ?? 0;
  const eligibleComplaintsCount = recordsData?.aggregateStats?.eligibleComplaintsCount ?? 0;

  // Use baseFilteredRecords directly (no tab filtering)
  const filteredRecords = baseFilteredRecords;

  // =============================================================================
  // Loading and Error States
  // =============================================================================

  const isLoading = isLoadingCollection || isLoadingRecords;
  const isFetching = isFetchingRecords;

  // Loading state
  if (isLoadingCollection) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/70 bg-card px-6 py-20 text-center shadow-[var(--shadow-card)]">
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Loading Records</h2>
          <p className="text-muted-foreground">Fetching collection data...</p>
        </div>
      </div>
    );
  }

  // Collection-level error (unrecoverable without reload)
  if (collectionError) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/70 bg-card px-6 py-20 text-center shadow-[var(--shadow-card)]">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Records</h2>
          <p className="text-muted-foreground max-w-md">
            {collectionError.message}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Missing collection ID state
  if (!collectionId) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/70 bg-card px-6 py-20 text-center shadow-[var(--shadow-card)]">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--go-bittersweet)]/10 text-[var(--go-bittersweet)]">
            <AlertCircle className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">No Collection Selected</h2>
          <p className="text-muted-foreground max-w-md">
            Please select a collection to view its records.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
      <div className="mx-auto flex w-full max-w-[1760px] flex-col gap-5">
        <header className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between xl:pr-28">
          <div className="space-y-2">
            <nav className="flex items-center gap-1 text-sm text-muted-foreground">
              <Link
                to="/v4/records"
                className="transition-colors hover:text-foreground"
              >
                Monitor
              </Link>
              <ChevronRight className="h-4 w-4" />
              <Link
                to={`/v4/records/${collectionId}`}
                className="font-medium text-foreground transition-colors hover:text-foreground"
              >
                {collection?.name || 'Loading...'}
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span>Records</span>
            </nav>
            <div className="space-y-0.5">
              <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-[-0.02em] text-foreground">
                <MonitoringDot status={collectionData?.collection.monitoringStatus ?? 'active'} />
                <span>{collection?.name || 'Records'}</span>
              </h1>
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const source = collectionData?.collection.source;
                  // Keep the same display-name + cadence vocabulary the card
                  // uses on Monitor Conduct so the records header reads as a
                  // longer version of the card line.
                  const skillNames: Record<string, string> = {
                    'intercom-reporting': 'Intercom',
                    'dataeq-analyse': 'DataEQ',
                  };
                  const cadenceLabels: Record<string, string> = {
                    every_30m: 'Every 30 min',
                    every_1h: 'Every hour',
                    every_3h: 'Every 3 hours',
                    every_6h: 'Every 6 hours',
                    every_12h: 'Every 12 hours',
                    every_24h: 'Daily',
                    daily: 'Daily',
                    weekly: 'Weekly',
                    monthly: 'Monthly',
                  };
                  // Pick the right "Last synced" source:
                  //   - Source-backed collection, never run → "Never" (the
                  //     monitor exists but its first tick hasn't completed,
                  //     so falling back to dataset.updatedAt would render a
                  //     concrete date that misrepresents the sync state).
                  //   - Source-backed collection, has run → source.lastRunAt
                  //     (imports update the monitor row, not the dataset).
                  //   - Manual collection (no source) → dataset.updatedAt.
                  let lastSyncNode: React.ReactNode;
                  if (source) {
                    lastSyncNode = source.lastRunAt ? (
                      <Timestamp value={source.lastRunAt} format="date-only" />
                    ) : (
                      'Never'
                    );
                  } else {
                    lastSyncNode = (
                      <Timestamp
                        value={collectionData?.collection.updatedAt}
                        format="date-only"
                      />
                    );
                  }
                  return (
                    <>
                      {totalRecords.toLocaleString()} records
                      {source && (
                        <>
                          {' · '}
                          {`${skillNames[source.skillName] || source.skillName} · ${cadenceLabels[source.cadence] || source.cadence}`}
                        </>
                      )}
                      {' · Last synced '}
                      {lastSyncNode}
                    </>
                  );
                })()}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Report buttons removed — reports now accessible from Collection Overview tiles (GO-152) */}
            <CollectionSettings
              complaintDetectionEnabled={complaintDetectionEnabled}
              vulnerabilityDetectionEnabled={vulnerabilityDetectionEnabled}
              billingTag={collectionData?.collection.billingTag ?? null}
              onComplaintDetectionChange={handleComplaintDetectionChange}
              onVulnerabilityDetectionChange={handleVulnerabilityDetectionChange}
              onBillingTagSave={handleBillingTagSave}
            />
          </div>
        </header>

        <RootCauseStatusBanner settings={organizationDisplaySettings} />

        {/* CollectionHeader stats cards removed — metrics now live on Collection Overview (GO-152) */}

        <section className="rounded-2xl border border-border/70 bg-card p-4 shadow-[var(--shadow-card)]">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="relative min-w-[200px] max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search summaries, customers, or verbatim..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 pl-9"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {vulnerabilityDetectionEnabled && (
                  <div className="min-w-[150px] rounded-xl border border-border/60 bg-card px-3 py-2">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      Vulnerable Customers
                    </p>
                    <p className="mt-1 text-base font-semibold text-foreground">
                      {vulnerableCount}
                    </p>
                  </div>
                )}
                {complaintDetectionEnabled && (
                  <div className="min-w-[150px] rounded-xl border border-border/60 bg-card px-3 py-2">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      Eligible Complaints
                    </p>
                    <p className="mt-1 text-base font-semibold text-foreground">
                      {eligibleComplaintsCount}
                    </p>
                  </div>
                )}
                <div className="flex shrink-0 items-center gap-1 rounded-full border border-border/70 bg-muted/30 p-1">
                  <Button
                    variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-8 rounded-full px-3"
                    onClick={() => setViewMode('table')}
                    aria-label="Table view"
                  >
                    <IconWrapper icon={FiList} className="size-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-8 rounded-full px-3"
                    onClick={() => setViewMode('cards')}
                    aria-label="Card view"
                  >
                    <IconWrapper icon={FiGrid} className="size-4" />
                  </Button>
                </div>
              </div>
            </div>

            <FilterStrip
              filters={filterState.filters}
              onFiltersChange={filterState.setFilters}
              onReset={filterState.reset}
              isLoading={isFetching}
              filterOptions={filterOptions}
              className="space-y-3"
            />
          </div>
        </section>

        <QueueTabs
          eligibleComplaintsCount={collectionData?.collection.eligibleComplaintsCount ?? 0}
          vulnerableCustomersCount={collectionData?.collection.vulnerableCustomersCount ?? 0}
          allRecordsCount={collectionData?.collection.recordCount ?? 0}
          activeQueue={activeQueue}
          onTabChange={(queue: QueueType) => {
            setActiveQueue(queue);
            setPage(1);
          }}
        />

        <section className="relative overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[var(--shadow-card)]">
          {isFetching && !isLoading && (
            <div className="absolute left-0 right-0 top-0 h-0.5 overflow-hidden bg-primary/20">
              <div className="h-full w-1/3 animate-[slide_1s_ease-in-out_infinite] bg-primary" />
            </div>
          )}
          {recordsError ? (
            <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertCircle className="h-6 w-6" />
              </div>
              <h3 className="mb-1 text-lg font-semibold text-foreground">Failed to load records</h3>
              <p className="max-w-md text-sm text-muted-foreground">
                {recordsError.message}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Try adjusting or clearing your filters above.
              </p>
            </div>
          ) : isLoadingRecords ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Loader2 className="mb-3 h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading records...</p>
            </div>
          ) : viewMode === 'table' ? (
            <RecordsTable
              records={filteredRecords}
              expandedRecordId={expandedRecordId}
              onRecordClick={handleRecordClick}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              complaintDetectionEnabled={complaintDetectionEnabled}
              vulnerabilityDetectionEnabled={vulnerabilityDetectionEnabled}
              organizationDisplaySettings={organizationDisplaySettings}
              onConfirmComplaint={handleConfirmComplaint}
              onRejectComplaint={handleRejectComplaint}
              onReclassify={handleReclassify}
              onStartSimulation={handleStartSimulation}
              reclassifyingRecordId={reclassifyingRecordId}
              startingSimulationRecordId={startingSimulationRecordId}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredRecords.map((record) => (
                <Card
                  key={record.id}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                  onClick={() => handleRecordClick(record.id)}
                >
                  <CardContent className="p-4">
                    <div className="mb-3 flex items-start justify-between">
                      <ConductScoreIndicator
                        score={record.conductScore}
                        size="small"
                        isProcessing={!!(record._progress?.stage && !['processed','classified','done','failed'].includes(String(record._progress.stage).toLowerCase()))}
                        processingStage={record._progress?.stage || record.processing_status}
                      />
                      <Timestamp
                        value={record.date}
                        format="date-only"
                        className="text-xs text-muted-foreground"
                      />
                    </div>

                    <h3 className="mb-1 line-clamp-1 font-medium text-foreground">
                      {record.customerName}
                    </h3>
                    <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                      {record.summary}
                    </p>

                    <div className="flex flex-wrap gap-1">
              {record.conductLabels.rootCauses.labels.slice(0, 2).map((label, idx) => (
                        <span
                          key={idx}
                          className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {label.id}
                        </span>
                      ))}
                      {record.conductLabels.rootCauses.labels.length > 2 && (
                        <span className="text-xs text-muted-foreground">
                          +{record.conductLabels.rootCauses.labels.length - 2}
                        </span>
                      )}
                    </div>
                  </CardContent>

                  {expandedRecordId === record.id && (
                    <div className="border-t border-border">
                      <RecordDetail
                        record={record}
                        complaintDetectionEnabled={complaintDetectionEnabled}
                        vulnerabilityDetectionEnabled={vulnerabilityDetectionEnabled}
                        organizationDisplaySettings={organizationDisplaySettings}
                        onConfirmComplaint={handleConfirmComplaint}
                        onRejectComplaint={handleRejectComplaint}
                        onReclassify={handleReclassify}
                        onStartSimulation={handleStartSimulation}
                        isReclassifying={reclassifyingRecordId === record.id}
                        isStartingSimulation={startingSimulationRecordId === record.id}
                      />
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {recordsData?.pagination && (
            <PaginationControls
              currentPage={page}
              totalPages={Math.ceil(totalRecords / pageSize)}
              totalItems={totalRecords}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              isLoading={isFetching}
              itemLabel={activeQueue === 'complaints' ? 'eligible complaints' : activeQueue === 'vulnerability' ? 'vulnerable customers' : 'records'}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export { RecordsPage };
export default RecordsPage;
